import { Outlet, redirect, RouteObject, useRouteError } from "react-router";
import { Root } from "src/Root";
import { OrgRoutes } from "src/router/routes/Orgs";
import { JobRoutes } from "src/router/routes/Jobs";
import { ScreenshareRoutes as ScreensharesRoutes } from "src/router/routes/Screenshares";
import { SignaturesRoutes } from "src/router/routes/Signatures";
import { SignedDocsRoutes } from "src/router/routes/SignedDocs";
import { ProtectedRoute } from "common/auth/ProtectedRoute";
import { Button } from "common/components/Button";
import { CustomLink } from "common/components/CustomLink";
import { SystemSettingsRoutes } from "src/router/routes/SystemSettings";
import { Auth0Login } from "common/components/Login/Auth0Login";
import { UserProfileRoutes } from "src/router/routes/UserProfile";
import { protectedRedirectLoader } from "src/router/loaders";
import { ROOT_ROUTE_ID } from "src/router/dataStrategy";
import { SpinnerOverlayOnly } from "common/components/Spinner";
import { DabellaLogin } from "common/components/Login/DabellaLogin";
import { FinanceRoutes } from "src/router/routes/finance";
import { formatUrlDate, parseUrlViewDate } from "common/helpers/CalendarRange";
import { Unauthorized } from "common/components/ErrorPages/Unauthorized";
import { NotFound } from "common/components/ErrorPages/NotFound";
import { Redirect } from "src/router/helper/Redirect";

const ErrorBoundary = () => {
  const error: any = useRouteError();
  console.error(error);
  return (
    <div>
      Root Route Not Found
      {error?.message && <p>{error.message}</p>}
      <CustomLink href="/">
        <Button analyticsAction="go to root">Go to Root</Button>
      </CustomLink>
    </div>
  );
};

export const RootRoutes: RouteObject[] = [
  {
    id: ROOT_ROUTE_ID,
    path: "/",
    shouldRevalidate: () => true,
    hydrateFallbackElement: <SpinnerOverlayOnly />,
    async loader(args) {
      const { messagesPanelLoader } = await import("src/router/loaders/Messages");
      const { isPanelOnlyNavigation } = await import("src/router/dataStrategy");

      if (isPanelOnlyNavigation(args.request.url)) {
        await messagesPanelLoader(args);
        return null;
      }

      const { pageTitleUpdaterLoader, resetJobStateLoader, resetOrgStateLoader } = await import("src/router/loaders");
      await Promise.all([
        resetJobStateLoader(args),
        resetOrgStateLoader(args),
        pageTitleUpdaterLoader(args),
        messagesPanelLoader(args),
      ]);
      return null;
    },
    element: <Root />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "login",
        async loader(args) {
          const { auth0LoginLoader } = await import("src/router/loaders");
          return auth0LoginLoader(args);
        },
        hydrateFallbackElement: <Auth0Login loading />,
        element: <Auth0Login loading />,
      },
      {
        path: "sso/login",
        async loader(args) {
          const { auth0LoginLoader } = await import("src/router/loaders");
          return auth0LoginLoader(args);
        },
        hydrateFallbackElement: <Auth0Login loading />,
        element: <Auth0Login loading />,
      },
      {
        path: "sso/callback",
        async loader(args) {
          const { auth0CallbackLoader } = await import("src/router/loaders");
          return auth0CallbackLoader(args);
        },
        hydrateFallbackElement: <Auth0Login loading />,
        element: <Auth0Login />,
      },
      {
        path: "sso/logout",
        async loader(args) {
          const { auth0LogoutLoader } = await import("src/router/loaders");
          return auth0LogoutLoader(args);
        },
        hydrateFallbackElement: <Auth0Login loading />,
        element: <Auth0Login />,
      },
      {
        path: "dabella/login",
        async loader(args) {
          const { dabellaLoginLoader } = await import("src/router/loaders");
          return dabellaLoginLoader(args);
        },
        hydrateFallbackElement: <DabellaLogin loading />,
        element: <DabellaLogin loading />,
      },
      {
        path: "okta/callback",
        async loader(args) {
          const { dabellaCallbackLoader } = await import("src/router/loaders");
          return dabellaCallbackLoader(args);
        },
        hydrateFallbackElement: <DabellaLogin loading />,
        element: <DabellaLogin />,
      },
      {
        path: "okta/logout",
        async loader(args) {
          const { oktaLogoutLoader } = await import("src/router/loaders");
          return oktaLogoutLoader(args);
        },
        hydrateFallbackElement: <DabellaLogin loading />,
        element: <DabellaLogin />,
      },
      {
        path: "logout",
        async loader(args) {
          const { auth0LogoutLoader } = await import("src/router/loaders");
          return auth0LogoutLoader(args);
        },
        hydrateFallbackElement: <Auth0Login loading />,
        element: <Auth0Login />,
      },
      {
        path: "signatures",
        // Only :id route is needed in the main app
        children: [SignaturesRoutes[1]],
      },
      {
        path: "screen_shares",
        children: ScreensharesRoutes,
      },
      {
        path: "terms",
        async loader(args) {
          const { termsLoader } = await import("src/router/loaders");
          return termsLoader(args);
        },
      },
      {
        path: "terms-conditions",
        async loader(args) {
          const { termsLoader } = await import("src/router/loaders");
          return termsLoader(args);
        },
      },
      {
        path: "404",
        element: <NotFound />,
      },
      {
        // Authentication Protected Routes
        path: "",
        shouldRevalidate: () => true,
        loader: protectedRedirectLoader,
        element: <ProtectedRoute element={<Outlet />} />,
        children: [
          {
            path: "profile",
            shouldRevalidate: () => false,
            async loader(args) {
              const { userProfileLoader } = await import("src/router/loaders/orgs/Users");
              return userProfileLoader(args);
            },
            async lazy() {
              const { Profile } = await import("src/user/Profile");
              return { element: <Profile /> };
            },
            children: UserProfileRoutes,
          },
          {
            path: "orgs",
            shouldRevalidate: () => false,
            async loader() {
              const { checkOrgsCache } = await import("src/router/loaders/Orgs");
              return checkOrgsCache();
            },
            children: OrgRoutes,
          },
          {
            path: "jobs",
            async loader(args) {
              const { rootRedirectLoader } = await import("src/router/loaders");
              return rootRedirectLoader(args);
            },
            children: JobRoutes,
          },
          {
            path: "calendar",
            shouldRevalidate: ({ currentUrl, nextUrl }) => {
              // Compare RESOLVED view/date (parseUrlViewDate fills missing/invalid
              // values with canonical defaults) so that mount-time normalization
              // — which only canonicalizes raw URL strings without changing what
              // the loader actually fetched — does not trigger a redundant
              // revalidation. The user_ids comparison stays on raw strings to
              // preserve the empty-string-vs-missing-key sentinel distinction.
              const curr = parseUrlViewDate(currentUrl.searchParams);
              const next = parseUrlViewDate(nextUrl.searchParams);
              return (
                curr.urlView !== next.urlView ||
                formatUrlDate(curr.date) !== formatUrlDate(next.date) ||
                currentUrl.searchParams.get("user_ids") !== nextUrl.searchParams.get("user_ids")
              );
            },
            async loader(args) {
              const { calendarLoader } = await import("src/router/loaders/Calendar");
              return calendarLoader(args);
            },
            async lazy() {
              const { Calendar } = await import("common/components/Calendar");
              return { element: <Calendar /> };
            },
            children: [
              {
                path: "new/edit",
                async lazy() {
                  const { EventEditModal } = await import("src/jobs/Job/AppointmentsTab/EventEditModal");
                  return { element: <EventEditModal /> };
                },
              },
              {
                path: ":eventId/edit",
                async lazy() {
                  const { EventEditModal } = await import("src/jobs/Job/AppointmentsTab/EventEditModal");
                  return { element: <EventEditModal /> };
                },
                async loader(args) {
                  const { eventLoader } = await import("src/router/loaders/jobs/Event");
                  return eventLoader(args);
                },
              },
              {
                path: ":eventId/invite",
                async lazy() {
                  const { SendInviteModal } = await import("src/jobs/Job/AppointmentsTab/SendInviteModal");
                  return { element: <SendInviteModal /> };
                },
              },
            ],
          },
          {
            path: "403",
            async loader(args) {
              const { accessesLoader } = await import(
                /* webpackChunkName: "orgs-cache-accesses-loader" */ "src/router/loaders/Orgs"
              );
              return accessesLoader(args);
            },
            element: <Unauthorized />,
          },
          {
            path: "finance",
            async lazy() {
              const { Finance } = await import("src/finance");
              return { element: <Finance /> };
            },
            children: FinanceRoutes,
          },
          {
            path: "payments",
            async lazy() {
              const { Payments } = await import("common/components/Payments");
              return { element: <Payments /> };
            },
          },
          {
            path: "signed_docs",
            children: SignedDocsRoutes,
          },
          {
            path: "system_settings",
            children: SystemSettingsRoutes,
          },
          {
            path: "tasks",
            async lazy() {
              const { Tasks } = await import("src/tasks/index");
              return { element: <Tasks /> };
            },
          },
          {
            path: "",
            async loader() {
              return redirect("/jobs");
            },
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Redirect to="/jobs" />,
  },
];
